/**
 * Configurações do formulário › respostas (2026-09-30):
 *  - "Aceitar mais de uma resposta da mesma pessoa" — desligado, a mesma
 *    pessoa (pelo contato) não envia de novo; só com os dados de contato;
 *  - "Mostrar barra de progresso" na página pública;
 *  - "E-mail de confirmação" para quem respondeu — pelo canal de e-mail da
 *    empresa, com assunto e texto próprios e variáveis;
 *  - "Avisar a equipe" — as pessoas escolhidas recebem aviso no sino e no
 *    celular a cada resposta.
 */
import React, { useMemo } from 'react';
import { FORM_CONFIRMATION_EMAIL_VARIABLES, ProviderId, type FormSettings } from '@troia-v3/types';
import { Input, Select, Switch, Textarea, UsersMultiSelect, type SelectOption } from '@/shared/components/ui';
import { useChannelsDropdown } from '@/modules/channels/hooks/useChannelsDropdown';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';

interface FormResponseSettingsSectionProps {
  value: FormSettings;
  onChange: (value: FormSettings) => void;
}

const muted: React.CSSProperties = { color: 'var(--theme-muted-text)' };

export const FormResponseSettingsSection: React.FC<FormResponseSettingsSectionProps> = ({ value, onChange }) => {
  const set = (partial: Partial<FormSettings>) => onChange({ ...value, ...partial });
  const pedeContato = value.contactData?.enabled === true;
  const email = value.confirmationEmail ?? { enabled: false };
  const avisos = value.notifications ?? { enabled: false, userIds: [] };

  // Só busca canais/pessoas com a opção ligada (SEMPRE #10).
  const { data: canais = [] } = useChannelsDropdown({ enabled: email.enabled });
  const { data: pessoas = [], isLoading: carregandoPessoas } = useUsersDropdown({ status: 'active', enabled: avisos.enabled });

  // O envio usa o provedor Resend — outros canais de e-mail recusariam.
  const opcoesDeCanal = useMemo<SelectOption[]>(
    () => [
      { value: '', label: 'Escolha o canal de e-mail' },
      ...canais
        .filter((c) => c.providerId === ProviderId.EMAIL_RESEND)
        .map((c) => ({ value: c.id, label: `${c.name} · ${c.identifier}` })),
    ],
    [canais],
  );

  return (
    <div className="space-y-5" data-testid="form-response-settings">
      <Switch
        label="Aceitar mais de uma resposta da mesma pessoa"
        description={pedeContato
          ? 'Desligado, quem já respondeu não consegue enviar de novo (reconhecido pelo telefone ou e-mail).'
          : 'Para impedir respostas repetidas, ligue "Exigir dados de contato" — é por eles que a pessoa é reconhecida.'}
        checked={value.allowMultipleResponses}
        onChange={(allowMultipleResponses) => set({ allowMultipleResponses })}
        disabled={!pedeContato}
        data-testid="form-settings-allow-multiple"
      />

      <Switch
        label="Mostrar barra de progresso"
        description="Barra no topo da página pública com quantas perguntas já foram respondidas."
        checked={value.showProgressBar}
        onChange={(showProgressBar) => set({ showProgressBar })}
        data-testid="form-settings-progress-bar"
      />

      <div className="space-y-3">
        <Switch
          label="E-mail de confirmação"
          description="Quem respondeu recebe um e-mail, pelo canal de e-mail da empresa."
          checked={email.enabled}
          onChange={(enabled) => set({ confirmationEmail: { ...email, enabled } })}
          data-testid="form-settings-confirmation-email"
        />
        {email.enabled && (
          <div className="space-y-3 pl-1" data-testid="form-settings-confirmation-email-fields">
            {!pedeContato || value.contactData?.requireEmail !== true ? (
              <p className="text-xs rounded-lg px-3 py-2" style={{ backgroundColor: 'var(--theme-warning-subtle)', color: 'var(--theme-warning-fg)' }} data-testid="form-settings-confirmation-email-warning">
                {pedeContato
                  ? 'O e-mail só sai para quem informar o e-mail em "Seus dados" (hoje ele é opcional).'
                  : 'Ligue "Exigir dados de contato" com o e-mail — sem ele não há para quem mandar.'}
              </p>
            ) : null}
            <Select
              label="Canal de e-mail"
              options={opcoesDeCanal}
              value={email.channelId ?? ''}
              onChange={(channelId) => set({ confirmationEmail: { ...email, ...(channelId ? { channelId } : { channelId: undefined }) } })}
              data-testid="form-settings-confirmation-email-channel"
            />
            <label className="block text-sm font-medium -mb-2" style={{ color: 'var(--theme-card-text)' }} htmlFor="form-settings-email-subject">
              Assunto
            </label>
            <Input
              id="form-settings-email-subject"
              value={email.subject ?? ''}
              onChange={(e) => set({ confirmationEmail: { ...email, subject: e.target.value } })}
              placeholder="Recebemos sua resposta, {{nome}}"
              data-testid="form-settings-confirmation-email-subject"
            />
            <Textarea
              label="Mensagem"
              value={email.body ?? ''}
              onChange={(e) => set({ confirmationEmail: { ...email, body: e.target.value } })}
              rows={6}
              placeholder={'Olá, {{nome}}!\nObrigado por responder o {{formulario}}.\n\n{{respostas}}'}
              data-testid="form-settings-confirmation-email-body"
            />
            <p className="text-xs" style={muted}>
              Variáveis: {FORM_CONFIRMATION_EMAIL_VARIABLES.map((v) => `{{${v}}}`).join(', ')}. {'{{respostas}}'} vira a lista de perguntas com as respostas da pessoa.
            </p>
          </div>
        )}
      </div>

      <div className="space-y-3">
        <Switch
          label="Avisar a equipe"
          description="As pessoas escolhidas recebem aviso no sino e no celular a cada resposta."
          checked={avisos.enabled}
          onChange={(enabled) => set({ notifications: { ...avisos, enabled } })}
          data-testid="form-settings-notifications"
        />
        {avisos.enabled && (
          <div className="pl-1" data-testid="form-settings-notifications-users">
            <UsersMultiSelect
              users={pessoas}
              selectedUserIds={avisos.userIds}
              onChange={(userIds) => set({ notifications: { ...avisos, userIds } })}
              placeholder="Escolher quem avisar"
              loading={carregandoPessoas}
            />
          </div>
        )}
      </div>
    </div>
  );
};
